import {RequestMethod} from '@gl/lit/common/constants/index.js';
import {reviewAudiencesLocale} from '@gl/lit/common/locale/review_audiences.js';
import {FlyErrorWarning} from '@gl/lit/utils/errors.js';
import {fetcher} from '@gl/lit/utils/http.js';
import {
  renderErrorNotification,
  renderNotification,
} from '@gl/lit/utils/notifications.js';
import {locale} from '@gl/lit/common/locale/index.js';
import {AlertIntent} from '@gl/lit/components/base/alert/index.js';
import type {
  Audience,
  AudienceTemplate,
  DestinationFilter,
} from '@gl/lit/types/audiences.js';
import {audienceBuilder} from '@gl/lit/builders/audience_builder/index.js';
import type {AudienceRefreshSizeResponse} from '@gl/lit/types/audiences.js';
import {FlyErrorCritical} from '@gl/lit/utils/errors.js';

export type AudienceLockResponse = {
  data: {
    audience: AudienceTemplate;
  };
};

export interface GetDestinationFiltersParams {
  dataset_group_id: string | number;
  id: string | number;
  queryString: string | number;
}
class AudiencesService {
  /**
   * Approve an audience
   */
  async lockAudience({
    locked,
    dataset_group_id,
    audienceId,
  }: {
    locked: boolean;
    dataset_group_id: number;
    audienceId: number;
  }) {
    const url = `/api/v2/dataset_groups/${dataset_group_id}/audiences/${audienceId}`;
    try {
      const response = await fetcher<AudienceLockResponse>(url, {
        method: RequestMethod.PATCH,
        body: {
          audience: {
            locked,
          },
        },
      });
      return response;
    } catch (e) {
      renderErrorNotification(e);
    }
  }
  async archiveAudience(params: {
    redirectUrl: string;
    postUrl: string;
    audience: Audience | AudienceTemplate;
    formToken: string;
  }): Promise<void> {
    const formData = new FormData();
    formData.append('_method', 'patch');
    formData.append('authenticity_token', params.formToken);

    try {
      await fetcher<void>(params.postUrl, {
        method: RequestMethod.PATCH,
        headers: {
          'X-CSRF-Token': params.formToken,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
        responseParser: null,
      });
      location.href = params.redirectUrl;
    } catch (err) {
      renderNotification(
        reviewAudiencesLocale[locale.current].review.error,
        AlertIntent.DANGER
      );
      new FlyErrorWarning('Error archiving audience').register(
        'Error in archive audience service'
      );
      throw err;
    }
  }

  async getDestinationFilters({
    dataset_group_id,
    id,
    queryString,
  }: GetDestinationFiltersParams): Promise<DestinationFilter[]> {
    const url = `/v2/dataset_groups/${dataset_group_id}/audiences/${id}/destination_filters${queryString}`;
    try {
      return await fetcher<DestinationFilter[]>(url, {
        method: RequestMethod.GET,
      });
    } catch (e) {
      renderErrorNotification(e);
      return [];
    }
  }

  async refreshAudienceSize(audienceId: string | number) {
    const url = `/v2/audiences/${audienceId}/refresh_size`;

    try {
      const {data} = await fetcher<AudienceRefreshSizeResponse>(url, {
        method: RequestMethod.PATCH,
      });

      if (data) {
        if (audienceBuilder.insights) {
          audienceBuilder.insights.size = data.count;
          audienceBuilder.insights.updatedAt = new Date(data.updated_at);
          audienceBuilder.insights.isUpToDate = true;
        }
        if (audienceBuilder.audience) {
          audienceBuilder.audience.size = data.count;
        }
        audienceBuilder.emitInitializedEvent();
      }
    } catch (err) {
      new FlyErrorCritical(
        'Unable to refresh audience size.',
        {
          label: 'request',
          data: `PATCH ${url}`,
        },
        {
          label: 'response',
          data: err,
        }
      ).register('Error in inspector root.');

      console.error(err);
    }
  }
}

export const audiencesService = new AudiencesService();
